import React from "react"
import ScreenReaderText from "@components/screen-reader-text"
import ReadMore from "@components/read-more"
import { formatPrice } from "@src/utils/products/pricing"
import { useProductStore } from "@src/hooks/useStore"
import ReviewsTeaser from "@src/components/reviews/reviews-teaser"

const ProductHeader = ({
  title,
  summary = "",
  rating,
  reviewCount
}) => {
  const { price, priceRaw, compareAtPrice, discountDisplay, couponDiscountDisplay } = useProductStore(["price", "priceRaw", "compareAtPrice", "discountDisplay", "couponDiscountDisplay"]);

  const { amount, coupon } = couponDiscountDisplay;
  const formattedRating = parseFloat(rating?.toFixed(1))
  
  return (
    <div className="flex flex-col gap-y-5" tabIndex={0}>
      <div className="hidden invisible md:flex md:visible flex-col items-stretch">
        <div className="flex w-full items-start gap-1 justify-between">
          <h1 className="text-[40px]">
            <ScreenReaderText srText={`Product title: ${title}`} className="text-inherit" defaultText={title} />
          </h1>

          {price && !coupon && <span className="headline text-[40px] text-right">
            <ScreenReaderText srText={`Discounted Price: ${price}`} className="text-inherit" defaultText={price.replace(/\.00$/, '')} />
          </span>}
          {coupon && <span className="headline text-[40px] text-right">
            <ScreenReaderText srText={`Discounted Price: ${formatPrice({ price: priceRaw - amount })}`} className="text-inherit" defaultText={formatPrice({ price: priceRaw - amount })} />
          </span>}
        </div>

        <div className="flex items-center justify-between pt-2 invisible md:visible">
          <ReviewsTeaser rating={formattedRating} reviewCount={reviewCount} starsFillColor={"#191818"} isPDP={true} />
          {compareAtPrice && !coupon && <div className="flex items-center gap-x-5">
            <p className="rounded-3xl px-2 py-1 text-xs leading-4 text-white font-normal bg-steadfast-red">
              <ScreenReaderText srText={`Discount Amount: ${discountDisplay}`} defaultText={'Save ' + discountDisplay} className="text-inherit whitespace-nowrap" />
            </p>
            <p className="subquote font-medium text-darker-gray relative px-1 after:flex after:user-select-none after:border-b-1 after:w-full after:border-solid after:border-black after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2" >
              <ScreenReaderText srText={`Original Price: ${compareAtPrice}`} className="text-inherit" defaultText={compareAtPrice} />
            </p>
          </div>}
          {coupon && <div className="flex items-center gap-x-5">
            <p className="rounded-3xl px-2 py-1 text-xs leading-4 text-white font-normal bg-steadfast-red">
              <ScreenReaderText srText={`Discount Amount: ${formatPrice({ price: amount })}`} defaultText={formatPrice({ price: amount }) + " discount applied"} className="text-inherit whitespace-nowrap" />
            </p>
            <p className="subquote font-medium text-darker-gray relative px-1 after:flex after:user-select-none after:border-b-1 after:w-full after:border-solid after:border-black after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2" >
              <ScreenReaderText srText={`Original Price: ${compareAtPrice}`} className="text-inherit" defaultText={compareAtPrice} />
            </p>
          </div>}
        </div>
      </div>

      <div className="flex visible md:hidden md:invisible flex-col items-stretch gap-y-2.5">
        <div className="flex flex-col gap-y-2.5 md:invisible">
          <ReviewsTeaser rating={formattedRating} reviewCount={reviewCount} starsFillColor={"#191818"} isPDP={true} />
          <h1 className="text-[32px]">
            <ScreenReaderText srText={`Product title: ${title}`} defaultText={title} className="text-inherit" />
          </h1>
        </div>

        <div className="flex flex-col gap-y-2.5">
          {price && !coupon && <span className="headline text-[24px]">
            <ScreenReaderText srText={`Discounted Price: ${price.replace(/\.00$/, '')}`} defaultText={price.replace(/\.00$/, '')} className="text-inherit" />
          </span>}
          {coupon && <span className="headline text-[24px]">
            <ScreenReaderText srText={`Discounted Price: ${formatPrice({ price: priceRaw - amount })}`} defaultText={formatPrice({ price: priceRaw - amount })} className="text-inherit" />
          </span>}
          {compareAtPrice && !coupon && <div className="flex items-center gap-x-2.5">
            <p className="subquote font-medium text-darker-gray relative px-1 after:flex after:user-select-none after:border-b-1 after:w-full after:border-solid after:border-black after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2">
              <ScreenReaderText srText={`Original Price: ${compareAtPrice}`} defaultText={compareAtPrice} className="text-inherit" />
            </p>
            <p className="rounded-3xl px-2 py-1 text-xs leading-4 text-white font-normal bg-steadfast-red" >
              <ScreenReaderText srText={`Discount Amount: ${discountDisplay}`} defaultText={'Save ' + discountDisplay} className="whitespace-nowrap text-inherit" />
            </p>
          </div>}
          {coupon && <div className="flex items-center gap-x-2.5">
            <p className="subquote font-medium text-darker-gray relative px-1 after:flex after:user-select-none after:border-b-1 after:w-full after:border-solid after:border-black after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2">
              <ScreenReaderText srText={`Original Price: ${compareAtPrice}`} defaultText={compareAtPrice} className="text-inherit" />
            </p>
            <p className="rounded-3xl px-2 py-1 text-xs leading-4 text-white font-normal bg-steadfast-red" >
              <ScreenReaderText srText={`Discount Amount: ${formatPrice({ price: amount })}`} defaultText={formatPrice({ price: amount }) + " discount applied"} className="whitespace-nowrap text-inherit" />
            </p>
          </div>}
        </div>

      </div>

      {summary && <ReadMore text={summary} />}
    </div>
  )
}

export default ProductHeader
